// ── What the editor column shows about one submission (`EX-EDR01`) ──────────
// The pill, the test strip and the result card all read the same few facts
// about the submission on screen, and they read them from three different
// payloads: a practice submission (`full`, per-test rows), a contest one
// (`count_only`, `hidden {passedCount, totalCount}`, `docs/29` §6.1) and a
// staff rehearsal (`full`). This file turns any of them into one shape, so the
// three components never learn which surface they are on.
//
// **The count is never inferred from the test rows on a contest.** A contest
// row carries no `testResults`; if one ever did, `numbered` is still the
// caller's decision (Wave 23 decision 4: a contest's boxes carry no numbers).

import type { JudgeVerdict, RunTwiceSummary, StudentTestResult } from "@/features/quiz/coding-task-api";
import type { GradedStatus } from "./SubmissionResult";

/** One submission as the column sees it, whatever surface it came from. */
export type WorkspaceSubmissionView = {
  status: GradedStatus;
  verdict: JudgeVerdict | null;
  /** Per-test rows, where the feedback policy sends them (practice, rehearsal). */
  testResults: StudentTestResult[] | null;
  /** The `count_only` summary, where the policy withholds the rows (contest). */
  hidden: { passedCount: number; totalCount: number } | null;
  timeMs: number | null;
  memoryKb: number | null;
  languageKey: string | null;
  languageVersion: string | null;
  createdAt: string | null;
  compileOutput: string | null;
  /** `EX-RTW03`: a contest row's run-twice summary; absent everywhere else. */
  runTwice?: RunTwiceSummary | null;
  /** `EX-FNC05`: a function problem's submission, whose compiler names `solution.*`, not `main.*`. */
  functionProblem?: boolean;
};

/** How the pill reads, in order of what the contestant needs to know. */
export type PillState =
  | { kind: "none" }
  | { kind: "queued" }
  | { kind: "judging" }
  | { kind: "compile" }
  | { kind: "verdict"; verdict: JudgeVerdict; passed: number | null; total: number | null };

export function isCompileError(view: Pick<WorkspaceSubmissionView, "status" | "verdict">): boolean {
  return view.status === "completed" && view.verdict === "compilation_error";
}

/**
 * Tests passed out of tests counted, or `null` when there is nothing to count.
 *
 * `totalCount` from the server counts skipped tests (`judge-feedback.ts`), and
 * so does this from the rows, so both surfaces print the same denominator for
 * the same submission. A compilation error has no count: it ran no test, and
 * "0 of 0 tests passed" was the line this ticket exists to remove.
 */
export function testCounts(view: WorkspaceSubmissionView): { passed: number; total: number } | null {
  if (view.status !== "completed" || isCompileError(view)) return null;
  if (view.testResults && view.testResults.length > 0) {
    return {
      passed: view.testResults.filter((row) => row.verdict === "accepted").length,
      total: view.testResults.length,
    };
  }
  if (view.hidden && view.hidden.totalCount > 0) {
    return { passed: view.hidden.passedCount, total: view.hidden.totalCount };
  }
  return null;
}

export function pillStateOf(view: WorkspaceSubmissionView | null): PillState {
  if (!view) return { kind: "none" };
  if (view.status === "queued") return { kind: "queued" };
  if (view.status === "running") return { kind: "judging" };
  if (isCompileError(view)) return { kind: "compile" };
  const counts = testCounts(view);
  return {
    kind: "verdict",
    verdict: view.verdict ?? "internal_error",
    passed: counts?.passed ?? null,
    total: counts?.total ?? null,
  };
}

/**
 * The longest single test and the most memory any test used.
 *
 * The submission's own `timeMs`/`memoryKb` are already the maxima the judge
 * recorded; the rows are the fallback for a payload that only carries them.
 */
export function longestTimeMs(view: WorkspaceSubmissionView): number | null {
  if (view.timeMs != null) return view.timeMs;
  const times = (view.testResults ?? []).map((row) => row.timeMs).filter((ms): ms is number => ms != null);
  return times.length > 0 ? Math.max(...times) : null;
}

export function mostMemoryKb(view: WorkspaceSubmissionView): number | null {
  if (view.memoryKb != null) return view.memoryKb;
  const sizes = (view.testResults ?? []).map((row) => row.memoryKb).filter((kb): kb is number => kb != null);
  return sizes.length > 0 ? Math.max(...sizes) : null;
}

/**
 * Which attempt on this letter `target` is, counting from one.
 *
 * `docs/29`: a compilation error is not an attempt, so it neither has a number
 * nor moves the ones after it. `null` when the list the caller holds may not
 * reach back to the first submission (a page shorter than the total), because
 * a guessed "attempt 3" that is really attempt 53 is worse than no number.
 */
export function attemptNumberOf<Row extends { submissionId: string; createdAt: string; status: GradedStatus; verdict?: JudgeVerdict | null }>(
  rowsOnLetter: readonly Row[],
  target: Row,
  listIsComplete: boolean,
): number | null {
  if (!listIsComplete) return null;
  if (target.status === "completed" && target.verdict === "compilation_error") return null;
  const at = Date.parse(target.createdAt);
  return rowsOnLetter.filter(
    (row) =>
      !(row.status === "completed" && row.verdict === "compilation_error") &&
      (Date.parse(row.createdAt) < at || row.submissionId === target.submissionId),
  ).length;
}
